// State snapshot + canonical hashing for multiplayer: the host broadcasts a full snapshot at match start
// (and for reconnect/late-join), and every client sends a state hash at each turn boundary so a desync is
// caught immediately instead of drifting silently. Plain data helpers, no DOM, no RNG - safe in src/game.
//
// The hash uses a CANONICAL stringify (recursively sorted object keys): two logically-identical states
// must hash the same even if one was live-constructed and the other hydrated from JSON (property insertion
// order can differ between those two paths, and JSON.stringify follows insertion order).

import type { GameState } from './types';

// Transient, per-turn presentation fields: not part of the authoritative state (mirrors storage.SavedState).
type CoreState = Omit<GameState, 'replay' | 'replayBefore' | 'log' | 'messages' | 'selectedRegion'>;

/** The authoritative state as plain JSON (transient animation/UI fields stripped). Send this to
 *  bootstrap a joining/reconnecting client; feed it to `restoreSnapshot` on arrival. */
export function snapshotState(g: GameState): string {
  const { replay: _r, replayBefore: _rb, log: _l, messages: _m, selectedRegion: _s, ...rest } = g;
  return JSON.stringify(rest);
}

/** Rebuild a live GameState from a snapshot produced by `snapshotState`. */
export function restoreSnapshot(json: string): GameState {
  const core = JSON.parse(json) as CoreState;
  return { ...core, replay: [], replayBefore: [], log: [], messages: [], selectedRegion: null };
}

// JSON.stringify with objects' keys sorted at every depth, so logically-equal states serialize identically.
function canonical(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(',')}}`;
}

/** FNV-1a (32-bit) over the canonical form of the authoritative state. Cheap enough to run every turn;
 *  compared across clients at each turn boundary to detect a lockstep desync. */
export function hashState(g: GameState): number {
  const { replay: _r, replayBefore: _rb, log: _l, messages: _m, selectedRegion: _s, ...rest } = g;
  const s = canonical(rest);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
